import { useEffect, useMemo, useRef } from 'react';
import { matchPath } from 'react-router-dom';

import Intercom, { onShow, showArticle, showSpace, update } from '@intercom/messenger-js-sdk';
import { InitType } from '@intercom/messenger-js-sdk/dist/types';
import { useMediaQuery } from '@mui/material';
import { isFeatureEnabled } from 'common/utils';
import { useCan } from 'modules/authorization/gates';
import { AppRoutes } from 'modules/router';
import {
  INTERCOM_HIDDEN_ROUTES as CORE_HIDDEN_ROUTES,
  INTERCOM_PUBLIC_ROUTES as CORE_PUBLIC_ROUTES,
  INTERCOM_ARTICLE_MAP,
} from 'modules/router/Routes';
import { selectPathname } from 'modules/router/state/router.selectors';
import {
  INTERCOM_HIDDEN_ROUTES as NATTERS_HIDDEN_ROUTES,
  INTERCOM_PUBLIC_ROUTES as NATTERS_PUBLIC_ROUTES,
} from 'natters/routes';
import {
  INTERCOM_HIDDEN_ROUTES as NATALIE_HIDDEN_ROUTES,
  INTERCOM_PUBLIC_ROUTES as NATALIE_PUBLIC_ROUTES,
} from 'pages/Natalie/routes';
import { useAppSelector } from 'store';
import { useGetMessengerTokenQuery } from 'store/apis/intercom';
import { selectUserAccount } from 'user/api';

import type { Theme } from '@mui/material';

// Combine hidden routes from all modules
const INTERCOM_HIDDEN_ROUTES: readonly string[] = [
  ...CORE_HIDDEN_ROUTES,
  ...NATTERS_HIDDEN_ROUTES,
  ...NATALIE_HIDDEN_ROUTES,
];

// Combine public routes from all modules (for anonymous Intercom)
const INTERCOM_PUBLIC_ROUTES: readonly string[] = [
  ...CORE_PUBLIC_ROUTES,
  ...NATTERS_PUBLIC_ROUTES,
  ...NATALIE_PUBLIC_ROUTES,
];

export const useIntercom = () => {
  const userAccount = useAppSelector(selectUserAccount);
  const pathname = useAppSelector(selectPathname);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isCompact = useMediaQuery((theme: Theme) => theme.breakpoints.down('tablet'));
  const { allowed: canManageCommunity } = useCan('community:manage');
  const isOnShowRegistered = useRef(false);
  const lastLauncherVisibility = useRef<boolean | null>(null);

  const userId = userAccount?.id?.toString();

  // Fetch JWT from backend - skip if no user
  const { data: tokenData } = useGetMessengerTokenQuery(undefined, {
    skip: !userId,
  });

  const isOnHiddenRoute = INTERCOM_HIDDEN_ROUTES.some((route) => matchPath(pathname, { path: route }));
  const isOnPublicRoute = INTERCOM_PUBLIC_ROUTES.some((route) => matchPath(pathname, { path: route }));
  // Phone screens whose primary action is pinned to the bottom-right corner the launcher sits in.
  const isOnMobileStickyActionRoute =
    (isMobile && !!matchPath(pathname, { path: AppRoutes.EventDetails, exact: true })) ||
    // The report's bottom tab bar spans the width below the tablet breakpoint.
    (isCompact && !!matchPath(pathname, { path: [AppRoutes.EventReport, AppRoutes.NatalieReport] }));

  const intercomData = useMemo<InitType | null>(() => {
    const isIntercomEnabled = isFeatureEnabled('IntercomEnabled');

    if (!isIntercomEnabled) {
      return null;
    }

    // Authenticated user path
    if (userAccount && tokenData?.token) {
      // Show launcher only to those who manage the community, and not on hidden routes
      const shouldHideLauncher = !canManageCommunity || isOnHiddenRoute || isOnMobileStickyActionRoute;

      return {
        app_id: window._env_.INTERCOM_ID,
        intercom_user_jwt: tokenData.token,
        name: userAccount.displayName,
        hide_default_launcher: shouldHideLauncher,
        alignment: 'right',
        horizontal_padding: 12,
        vertical_padding: 12,
        avatar: {
          type: 'avatar',
          image_url: userAccount.avatarLink,
        },
      };
    }

    // Anonymous user path - only show on designated public routes
    if (isOnPublicRoute) {
      return {
        app_id: window._env_.INTERCOM_ID,
        hide_default_launcher: false,
        alignment: 'right',
        horizontal_padding: 12,
        vertical_padding: 12,
      };
    }

    return null;
  }, [userAccount, tokenData, canManageCommunity, isOnHiddenRoute, isOnPublicRoute, isOnMobileStickyActionRoute]);

  useEffect(() => {
    if (!intercomData) {
      return;
    }

    Intercom(intercomData);
    // Boot options alone do not reliably hide or restore an already mounted launcher after navigation.
    const hideLauncher = Boolean(intercomData.hide_default_launcher);
    if (lastLauncherVisibility.current !== null && lastLauncherVisibility.current !== hideLauncher) {
      update({ hide_default_launcher: hideLauncher });
    }
    lastLauncherVisibility.current = hideLauncher;

    // Register onShow callback only once after Intercom is initialized
    if (!isOnShowRegistered.current) {
      isOnShowRegistered.current = true;
      onShow(() => {
        const currentPath = window.location.pathname;
        for (const [pattern, articleId] of Object.entries(INTERCOM_ARTICLE_MAP)) {
          if (matchPath(currentPath, { path: pattern })) {
            showArticle(articleId);
            return;
          }
        }
        // No article mapping found - show home view to prevent cached article
        showSpace('home');
      });
    }
  }, [intercomData]);
};
